import React, { useState, useCallback } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress, Avatar, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createKid, clearError } from '../../store/slices/kidSlice';
import { fetchAgeSections } from '../../store/slices/ageSectionSlice';
import { fetchAvatars } from '../../store/slices/avatarSlice';
import Header from '../common/Header';
const SignUpChild = ({ onNext }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user, isAuthenticated, authInitialized } = useSelector((state) => state.auth);
  const { loading: kidLoading, error: kidError } = useSelector((state) => state.kid);
  const { ageSections, loading: ageLoading } = useSelector((state) => state.ageSection);
  const { avatars, loading: avatarLoading } = useSelector((state) => state.avatar);

  const [childData, setChildData] = useState({
    firstname: '',
    name: '',
    avatar_id: ''
    // Retirer ages_section_id - sera ajouté plus tard
  });
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState('info'); // 'info' ou 'avatar'

  // Charger les données nécessaires au montage
  const loadInitialData = useCallback(() => {
    dispatch(fetchAgeSections());
    dispatch(fetchAvatars());
  }, [dispatch]);
  
  React.useEffect(() => {
    loadInitialData();
  }, []);

  React.useEffect(() => {
    if (!authInitialized) return;
    if (!isAuthenticated || !user) {
      navigate('/auth');
    }
  }, [authInitialized, isAuthenticated, user, navigate]);

  const handleChange = (field, value) => {
    setChildData({ ...childData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!childData.firstname?.trim()) {
      newErrors.firstname = 'Le prénom est requis';
    }
    if (!childData.name?.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    // Retirer la validation de l'avatar
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Créer l'enfant avec les données du formulaire
      const kidDataWithUser = {
        ...childData,
        user_id: user.id,
        // S'assurer que avatar_id est défini (null si pas d'avatar)
        avatar_id: childData.avatar_id || null
      };

      console.log('Données envoyées:', kidDataWithUser);

      const result = await dispatch(createKid(kidDataWithUser)).unwrap();
      console.log('Résultat de création:', result);

      // Si création réussie, passer à l'étape suivante
      if (result) {
        onNext(result); // Passer l'objet enfant créé
      }
    } catch (error) {
      console.error('Erreur lors de la création de l\'enfant:', error);
      // Afficher l'erreur à l'utilisateur
      setErrors({ submit: error.message || 'Erreur lors de la création' });
    }
  };

  const handleAvatarSelect = (avatarId) => {
    setChildData({ ...childData, avatar_id: avatarId });
    if (errors.avatar_id) {
      setErrors({ ...errors, avatar_id: '' });
    }
  };

  if (!user) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <Typography>Redirection vers la connexion...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#FFF7E5',
        px: 2,
        py: 4
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '500px',
          mx: 'auto'
        }}
      >
     
      {/* Image de l'enfant */}
      <Box sx={{ textAlign: 'center', alignItems: 'start', mb: 3 }}>
        <img
          src="/assets/homepage.png"
          alt="Enfant joyeux"
          style={{
            width: '200px',
            height: '200px',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Titre */}
      <Typography
        variant="h4"
        sx={{
          fontWeight: 'bold',
          color: '#2C3E50',
          mb: 4,
          textAlign: 'center',
          fontSize: { xs: '1.5rem', sm: '2rem' },
          fontFamily: 'Achiko'
        }}
      >
        Créer le profil de votre enfant
      </Typography>

      {/* Affichage des erreurs */}
      {kidError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {kidError}
        </Alert>
      )}

      {errors.submit && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.submit}
        </Alert>
      )}

      {/* Formulaire d'informations */}
      <Box sx={{ mb: 4 }}>
        <TextField
          label="Prénom"
          variant="outlined"
          fullWidth
          value={childData.firstname || ''}
          onChange={(e) => handleChange('firstname', e.target.value)}
          error={!!errors.firstname}
          helperText={errors.firstname}
          sx={{
            backgroundColor: 'white',
            mt: 2,
            borderRadius: '15px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '15px',
            },
            '& .MuiInputBase-input': {
              py: 1.5,
              fontSize: '1rem'
            }
          }}
        />

        <TextField
          label="Nom de famille"
          variant="outlined"
          fullWidth
          value={childData.name || ''}
          onChange={(e) => handleChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
          sx={{
            backgroundColor: 'white',
            mt: 2,
            borderRadius: '15px',
            '& .MuiOutlinedInput-root': {
              borderRadius: '15px',
            },
            '& .MuiInputBase-input': {
              py: 1.5,
              fontSize: '1rem'
            }
          }}
        />

        {/* Sélection de la tranche d'âge */}
        <FormControl
          fullWidth
          sx={{
            mt: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: '15px',
            }
          }}
        >
        {/* <InputLabel>Tranche d'âge</InputLabel>
          <Select
            value={childData.ages_section_id || ''}
            onChange={(e) => handleChange('ages_section_id', e.target.value)}
            error={!!errors.ages_section_id}
            disabled={kidLoading || ageLoading}
            label="Tranche d'âge"
            sx={{ backgroundColor: 'white', borderRadius: '15px' }}
          >
            {ageSections.map((ageSection) => (
              <MenuItem key={ageSection.id} value={ageSection.id}>
                {ageSection.name}
              </MenuItem>
            ))}
          </Select> */}
          {errors.ages_section_id && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {errors.ages_section_id}
            </Typography>
          )}
        </FormControl>
      </Box>

     {/*
     
     
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            fontWeight: 'bold',
            color: '#784e3c',
            fontFamily: 'Achiko'
          }}
        >
          Choisissez un avatar pour votre enfant <span style={{color: 'red'}}>*</span>
        </Typography>


        {avatarLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: 'center'
          }}>
            {avatars.map((avatar) => (
              <Avatar
                key={avatar.id}
                src={avatar.image_url}
                sx={{
                  width: 80,
                  height: 80,
                  cursor: 'pointer',
                  border: childData.avatar_id === avatar.id ? '4px solid #784e3c' : '2px solid #ddd',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.1)',
                    border: '3px solid #784e3c'
                  }
                }}
                onClick={() => handleAvatarSelect(avatar.id)}
              >
                {!avatar.image_url && avatar.name?.charAt(0)}
              </Avatar>
            ))}
          </Box>
        )}


        {errors.avatar_id && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {errors.avatar_id}
          </Typography>
        )}


        {!childData.avatar_id && (
          <Typography variant="caption" color="#666" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
            👆 Cliquez sur un avatar pour le sélectionner
          </Typography>
        )}
      </Box>
     
     */}

      {/* Bouton de soumission */}
      <Button
        onClick={handleSubmit}
        variant="contained"
        fullWidth
        disabled={kidLoading || !childData.firstname?.trim() || !childData.name?.trim() }
        sx={{
          backgroundColor: '#784e3c',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '1rem',
          textTransform: 'none',
          borderRadius: '50px',
          py: 1.5,
          mt: 3,
          boxShadow: '0px 4px 10px rgba(235, 164, 91, 0.4)',
          fontFamily: 'Achiko',
          '&:hover': {
            backgroundColor: '#D89440',
            boxShadow: '0px 6px 15px rgba(235, 164, 91, 0.6)',
          },
          '&:disabled': {
            backgroundColor: '#ccc',
            color: '#666'
          }
        }}
      >
        {kidLoading ? (
          <>
            <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
            Création en cours...
          </>
        ) : (
          'Créer le profil et continuer'
        )}
      </Button>

      {/* Informations supplémentaires */}
      <Typography
        variant="body2"
        sx={{
          textAlign: 'center',
          color: '#666',
          mt: 2,
          maxWidth: '400px',
          mx: 'auto'
        }}
      >
        Toutes les informations sont nécessaires pour créer un profil personnalisé adapté à votre enfant.
      </Typography>
      </Box>
    </Box>
  );
};

export default SignUpChild;